import { Chart } from 'chart.js/auto';
import { ForecastWidgetConfig, WidgetRenderContext, WidgetRenderer } from '../types';
import { ForecastBinaryService } from '../../../../services/ForecastBinaryService';
import TierAccessService from '../../../../services/TierAccessService';

export class SunIntensityWidget implements WidgetRenderer {
  private getBadgeColor(flux: number): string {
    if (flux >= 1100) return '#8e24aa'; // Alpine violet (Extreme)
    if (flux >= 900) return '#e53935';  // Intense coral
    if (flux >= 650) return '#fb8c00';  // Strong orange
    if (flux >= 350) return '#ffb300';  // Moderate yellow
    if (flux >= 150) return '#c0ca33';  // Low olive
    return '#5c6bc0';                  // Dim / Twilight blue
  }

  public renderHtml(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): string {
    const title = widget.title || 'Sun Intensity & Solar Flux';
    const height = widget.options?.height || 120;
    const data = ctx.data;
    const canvasId = `poi-sun-intensity-canvas-${widget.id}`;
    const isBlocked = !data.timeLabels || data.timeLabels.length === 0;
    const badgeBg = this.getBadgeColor(data.maxSunIntensity);

    return `
      <div class="metric-card" style="background: rgba(255,255,255,0.03); border: 1px solid var(--border-light); border-radius: 8px; padding: 12px;">
        <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid rgba(255,255,255,0.05); padding-bottom: 6px; margin-bottom: 8px;">
          <h3 style="margin: 0; color: #f8fafc; font-size: 0.95rem;">☀️ ${title}</h3>
          ${!isBlocked ? `
          <div style="font-size: 0.85rem; font-weight: bold; padding: 3px 10px; border-radius: 6px; background: ${badgeBg}; color: #ffffff;">
            Peak: ${data.maxSunIntensity} W/m² (${data.sunIntensityCategory})
          </div>` : ''}
        </div>
        <div style="font-size: 0.75rem; color: #94a3b8; margin-bottom: 8px;">
          Hourly Downward Shortwave Solar Irradiance (W/m²)
        </div>
        <div style="height: ${height}px; position: relative;">
          <canvas id="${canvasId}"></canvas>
        </div>
      </div>
    `;
  }

  public init(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): void {
    const canvasId = `poi-sun-intensity-canvas-${widget.id}`;
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
    if (!canvas) return;

    const data = ctx.data;
    if (!data.timeLabels || data.timeLabels.length === 0) {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.style.display = 'none';
        parent.innerHTML = TierAccessService.buildTrendChartDenialHtml({
          denial: data?.accessDenial || ForecastBinaryService.lastAccessDenial,
          requiredTier: 'pro',
          featureLabel: 'hourly sun intensity & solar irradiance trend charts',
          compact: true
        });
      }
      return;
    }

    const sunChart = new Chart(canvas, {
      type: 'line',
      data: {
        labels: data.timeLabels,
        datasets: [
          {
            label: 'Sun Intensity (W/m²)',
            data: data.sunIntensityHourly,
            borderColor: '#fb8c00',
            backgroundColor: 'rgba(251, 140, 0, 0.2)',
            fill: true,
            tension: 0.4,
            pointRadius: 2
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: { ticks: { color: '#94a3b8' }, grid: { color: 'rgba(255,255,255,0.05)' } },
          y: {
            min: 0,
            ticks: { color: '#94a3b8' },
            grid: { color: 'rgba(255,255,255,0.05)' },
            title: { display: true, text: 'W/m²', color: '#fb8c00' }
          }
        },
        plugins: {
          legend: { labels: { color: '#f8fafc' } }
        }
      }
    });

    ctx.charts.push(sunChart);
  }
}
